import { createSignal, createEffect, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useParams } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useUserContext } from '../components/UserContextProvider';
import { useTenantPermission } from '../hooks/tenantPermissions';
import { useRoleParser } from '../hooks/permissions';
import { TenantRole } from '../models/Tenant';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import type { GrantTypeDto, TenantGrantEntryDto } from '../services/grantsApi';
import { refreshLicenses } from '../hooks/useLicense';
import votingStyles from './Voting.module.css';
import { firstError } from '../api/envelope';

export default function TenantGrants() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { userTenant, user } = useUserContext();
    const { hasRole } = useTenantPermission();
    const { parseRoles, roles } = useRoleParser();
    const params = useParams<{ id?: string }>();

    const isRoot = () => parseRoles(user()?.permissions ?? 0, roles)[roles.indexOf('Root')];
    const adminMode = () => !!params.id;
    const tenantId = () => params.id ?? userTenant()?.tenantId ?? null;
    const canManage = () => (adminMode() ? isRoot() : (hasRole(TenantRole.Admin) || isRoot()));
    const [tenantName, setTenantName] = createSignal<string>('');

    const [catalog, setCatalog] = createSignal<GrantTypeDto[]>([]);
    const [entries, setEntries] = createStore<TenantGrantEntryDto[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [members, setMembers] = createSignal<{ id: string; username: string }[]>([]);
    const [poolEdits, setPoolEdits] = createSignal<Record<string, number>>({});
    const [assignType, setAssignType] = createSignal('');
    const [assignUserId, setAssignUserId] = createSignal('');
    const [assignValue, setAssignValue] = createSignal(1);
    const [busy, setBusy] = createSignal(false);

    async function loadCatalog() {
        const res = await api.grants.catalog();
        if (isOk(res.data)) setCatalog((res.data.value.catalog ?? []) as GrantTypeDto[]);
    }

    async function loadSummary() {
        const id = tenantId();
        if (!id) return;
        const res = await api.grants.tenantSummary(id);
        if (!isOk(res.data)) return;
        setEntries(reconcile(res.data.value.entries as TenantGrantEntryDto[], { key: 'type' }));
        setLoading(false);
    }

    async function loadMembers() {
        const id = tenantId();
        if (!id) return;
        const res = adminMode()
            ? await api.tenantAdmin.getMembers(id)
            : await api.tenant.getMembers(id);
        if (isOk(res.data)) {
            const list = (res.data.value.members ?? []) as { userId: string; username?: string | null; user?: { username: string } }[];
            setMembers(list.map(m => ({ id: m.userId, username: m.username ?? m.user?.username ?? m.userId })));
        }
    }

    async function loadTenantName() {
        if (!adminMode()) return;
        const res = await api.tenantAdmin.getAllTenants();
        if (isOk(res.data)) {
            const list = (res.data.value.tenants ?? []) as { id: string; name: string }[];
            const t = list.find(x => x.id === tenantId());
            if (t) setTenantName(t.name);
        }
    }

    createEffect(() => {
        if (!tenantId()) return;
        setLoading(true);
        void loadCatalog();
        void loadSummary();
        void loadMembers();
        void loadTenantName();
    });

    useVisibilityPoll(() => { if (tenantId()) void loadSummary(); }, 15_000);

    async function setPool(type: string) {
        const id = tenantId();
        if (!id) return;
        const value = poolEdits()[type] ?? 0;
        setBusy(true);
        try {
            const res = await api.grants.setTenantPool(id, { type, value, note: null });
            if (isOk(res.data)) {
                toast?.info(t('GRANTS_POOL_SAVED'));
                refreshLicenses();
                await loadSummary();
            } else {
                toast?.error(firstError(res.data) ?? t('GRANT_SAVE_FAILED'));
            }
        } finally {
            setBusy(false);
        }
    }

    async function assign() {
        const id = tenantId();
        if (!id || !assignType() || !assignUserId() || assignValue() <= 0) return;
        setBusy(true);
        try {
            const res = await api.grants.assignFromPool(id, { userId: assignUserId(), type: assignType(), value: assignValue(), note: null });
            if (isOk(res.data)) {
                toast?.info(t('GRANTS_ASSIGNED'));
                setAssignUserId('');
                setAssignValue(1);
                refreshLicenses();
                await loadSummary();
            } else {
                toast?.error(firstError(res.data) ?? t('GRANT_ASSIGN_FAILED'));
            }
        } finally {
            setBusy(false);
        }
    }

    async function revoke(grantId: string) {
        if (!await showConfirm(t('GRANTS_REVOKE_CONFIRM'), true)) return;
        const res = await api.grants.revoke(grantId);
        if (isOk(res.data)) {
            toast?.info(t('GRANTS_REVOKED'));
            refreshLicenses();
            await loadSummary();
        }
    }

    function poolValueOf(entry: TenantGrantEntryDto): number {
        const edits = poolEdits();
        return edits[entry.type] ?? entry.poolValue;
    }

    return (
        <div class={votingStyles.container}>
            <div class={votingStyles.headerRow}>
                <h1>
                    <Text val="TENANT_GRANTS_TITLE" />
                    <Show when={adminMode() && tenantName()}>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-weight': 'normal' }}> &mdash; {tenantName()}</span>
                    </Show>
                </h1>
            </div>

            <Show when={!tenantId()}>
                <div class="fcard"><Text val="TENANT_NO_SELECTION" /></div>
            </Show>

            <Show when={tenantId() && !canManage()}>
                <div class="fcard"><Text val="TENANT_GRANTS_NEED_ADMIN" /></div>
            </Show>

            <Show when={tenantId() && canManage()}>
                <Show when={!loading()} fallback={
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <div class={votingStyles.skeleton} />
                        <div class={votingStyles.skeleton} />
                    </div>
                }>
                    <h2><Text val="TENANT_GRANTS_POOLS" /></h2>
                    <p style={{ color: 'var(--dark-text-muted)', 'margin-top': 0 }}>
                        <Text val="TENANT_GRANTS_POOLS_HELP" />
                    </p>
                    <div class="v" style={{ gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-2xl)' }}>
                        <For each={entries}>{(entry) => (
                            <div class="fcard">
                                <div class="h" style={{ 'justify-content': 'space-between', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span style={{ 'font-weight': 600 }}>{entry.displayName}</span>
                                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>{entry.description}</span>
                                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)' }}>
                                            <Text val="TENANT_GRANTS_ASSIGNED" />: {entry.assigned} / <Text val="TENANT_GRANTS_POOL" />: {entry.poolValue} ({entry.kind})
                                        </span>
                                    </div>
                                    <Show when={isRoot()}>
                                        <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                style={{ width: '5rem' }}
                                                value={poolValueOf(entry)}
                                                onInput={e => setPoolEdits({ ...poolEdits(), [entry.type]: parseInt(e.currentTarget.value) || 0 })}
                                            />
                                            <button class="btn btn-primary btn-sm" disabled={busy()} onClick={() => setPool(entry.type)}>
                                                <Text val="GRANTS_SAVE_POOL" />
                                            </button>
                                        </div>
                                    </Show>
                                </div>
                                <Show when={entry.assignments.length > 0}>
                                    <div style={{ 'margin-top': 'var(--spacing-md)' }}>
                                        <For each={entry.assignments}>{(a) => (
                                            <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', padding: 'var(--spacing-xs) 0', 'border-top': '1px solid var(--dark-border)' }}>
                                                <span><i class="fa-solid fa-user" style={{ 'margin-right': 'var(--spacing-sm)', color: 'var(--dark-text-muted)' }} />{a.username}</span>
                                                <span style={{ 'font-family': 'var(--font-family-mono)' }}>+{a.value}</span>
                                                <button class="btn btn-danger btn-sm" onClick={() => revoke(a.grantId)} title={t('GRANTS_REVOKE')}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        )}</For>
                                    </div>
                                </Show>
                            </div>
                        )}</For>
                    </div>

                    <h2><Text val="TENANT_GRANTS_ASSIGN" /></h2>
                    <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                <Text val="TENANT_GRANTS_TYPE" />
                            </span>
                            <select value={assignType()} onChange={e => setAssignType(e.currentTarget.value)}>
                                <option value="">--</option>
                                <For each={catalog()}>{(c) => (
                                    <option value={c.type}>{c.displayName}</option>
                                )}</For>
                            </select>
                        </label>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                <Text val="TENANT_GRANTS_USER" />
                            </span>
                            <select value={assignUserId()} onChange={e => setAssignUserId(e.currentTarget.value)}>
                                <option value="">--</option>
                                <For each={members()}>{(m) => (
                                    <option value={m.id}>{m.username}</option>
                                )}</For>
                            </select>
                        </label>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                <Text val="TENANT_GRANTS_VALUE" />
                            </span>
                            <input type="number" min="1" value={assignValue()} onInput={e => setAssignValue(parseInt(e.currentTarget.value) || 0)} />
                        </label>
                        <div class="h" style={{ 'justify-content': 'flex-end' }}>
                            <button type="button" class="btn btn-primary" disabled={busy() || !assignType() || !assignUserId() || assignValue() <= 0} onClick={assign}>
                                <i class="fa-solid fa-plus" /> <Text val="TENANT_GRANTS_ASSIGN" />
                            </button>
                        </div>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
